import React, { useState, useRef, useEffect } from 'react';
import { 
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  Monitor,
  MonitorOff,
  MessageSquare,
  FileText,
  Save,
  Clock,
  DollarSign,
  Shield,
  Circle,
  Square,
  Send,
  Settings,
  Maximize,
  Minimize,
  Users,
  UserPlus,
  UserX,
  Copy,
  CheckCircle,
  ChevronRight
} from 'lucide-react';
import { useLegalVideoConsultation } from '../../hooks/useLegalVideoConsultation';

interface AttorneyConsultationProps {
  roomId?: string;
  clientName?: string;
  caseType?: string;
  attorneyName?: string;
  firmName?: string;
  consultationId?: string;
  requestId?: string;
  onEndConsultation?: () => void;
  onSaveNotes?: (notes: any) => void;
  onScheduleFollowUp?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'attorney' | 'client' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

interface Participant {
  id: string;
  name: string;
  role: 'attorney' | 'client' | 'observer';
  status: 'connected' | 'waiting' | 'disconnected';
  joinedAt: Date;
}

export const AttorneyConsultation: React.FC<AttorneyConsultationProps> = ({
  roomId: initialRoomId,
  attorneyName = 'Attorney',
  firmName = 'Smith & Associates',
  requestId
}) => {
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const attorneyNameFromUrl = urlParams.get('attorneyName') || attorneyName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';

  // Use the legal video consultation hook
  const {
    room,
    participant,
    status,
    isConnected,
    isLoading,
    error,
    videoState,
    consultationTime,
    recordingConsent,
    formattedTime,
    billableAmount,
    joinAsAttorney,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    toggleRecording,
    updateRecordingConsent,
    endConsultation
  } = useLegalVideoConsultation({
    onConsultationEnd: (billingSummary) => {
      console.log('Consultation ended with billing:', billingSummary);
    },
    onError: (error) => {
      console.error('Video consultation error:', error);
    }
  });

  // UI states
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [showParticipants, setShowParticipants] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Mock participants for UI
  const [participants, setParticipants] = useState<Participant[]>([
    {
      id: '1',
      name: attorneyNameFromUrl,
      role: 'attorney',
      status: 'connected',
      joinedAt: new Date()
    }
  ]);

  // Consultation data
  const [caseNotes, setCaseNotes] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Legal consultation started. Attorney-client privilege is now in effect.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  // Refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize as attorney when component mounts
  useEffect(() => {
    const initializeAsAttorney = async () => {
      if (roomId) {
        try {
          console.log('[AttorneyConsultation] Joining as attorney:', roomId);
          await joinAsAttorney(roomId, attorneyNameFromUrl);
        } catch (error) {
          console.error('Failed to join as attorney:', error);
        }
      }
    };

    initializeAsAttorney();
  }, []); // Empty dependency array to run only once

  // Generate client invitation link
  const getClientInvitationLink = () => {
    const baseUrl = window.location.origin;
    const clientUrl = `${baseUrl}/templates/legal/consultation/client?roomId=${roomId}&requestId=${requestIdFromUrl}`;
    return clientUrl;
  };

  // Copy invitation link
  const copyInvitationLink = async () => {
    try {
      await navigator.clipboard.writeText(getClientInvitationLink());
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 3000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  // Initialize local media stream
  useEffect(() => {
    const initializeMedia = async () => {
      try {
        if (videoState.localVideo || videoState.localAudio) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: videoState.localVideo,
            audio: videoState.localAudio
          });
          
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
          
          console.log('[AttorneyConsultation] Local media initialized');
        }
      } catch (error) {
        console.error('Failed to initialize local media:', error);
      }
    };

    initializeMedia();
    
    return () => {
      if (localVideoRef.current?.srcObject) {
        const stream = localVideoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoState.localVideo, videoState.localAudio]);

  const handleRecordingToggle = async () => {
    try {
      await toggleRecording();
      
      const recordingMessage: ChatMessage = {
        id: Date.now().toString(),
        sender: 'system',
        message: videoState.recording 
          ? 'Recording stopped. Recording saved to case file.'
          : 'Recording started with client consent.',
        timestamp: new Date(),
        type: 'system'
      };
      setChatMessages(prev => [...prev, recordingMessage]);
    } catch (error) {
      console.error('Recording toggle failed:', error);
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: 'attorney',
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const handleEndConsultation = async () => {
    if (window.confirm('Are you sure you want to end this consultation?')) {
      try {
        await endConsultation();
        window.history.pushState({}, '', '/templates/legal/attorney');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (error) {
        console.error('Failed to end consultation:', error);
      }
    }
  };

  return (
    <div className={`h-screen bg-gray-900 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold flex items-center">
              <Shield size={16} className="mr-2 text-blue-400" />
              Attorney View - {firmName}
            </h2>
            <p className="text-sm text-gray-300">Room ID: {roomId}</p>
          </div>
          <div className="bg-green-900 text-green-200 px-3 py-1 rounded-full text-xs flex items-center">
            <Shield size={12} className="mr-1" />
            Privileged Communication
          </div>
          <div className="bg-blue-900 text-blue-200 px-3 py-1 rounded-full text-xs flex items-center">
            <Users size={12} className="mr-1" />
            Host Controls
          </div>
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer and Billing */}
          <div className="text-right">
            <div className="flex items-center text-lg font-mono">
              <Clock size={16} className="mr-2" />
              {formattedTime}
            </div>
            <div className="flex items-center text-sm text-gray-300">
              <DollarSign size={12} className="mr-1" />
              ${billableAmount.toFixed(2)}
            </div>
          </div>

          {/* Recording Status */}
          {videoState.recording && (
            <div className="bg-red-900 text-red-200 px-3 py-1 rounded-full text-xs flex items-center animate-pulse">
              <Circle size={8} className="mr-2 fill-current" />
              Recording
            </div>
          )}

          {/* Settings */}
          <button className="p-2 hover:bg-gray-700 rounded-lg">
            <Settings size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          {/* Remote Video (Client) */}
          <video
            ref={remoteVideoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
          />
          
          {/* Local Video (Attorney) - Picture in Picture */}
          <div className="absolute top-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600">
            <video
              ref={localVideoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            {!videoState.localVideo && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <VideoOff className="text-gray-400" size={24} />
              </div>
            )}
          </div>

          {/* Screen Share Indicator */}
          {videoState.screenShare && (
            <div className="absolute top-4 left-4 bg-blue-900 text-blue-200 px-3 py-1 rounded-lg text-sm flex items-center">
              <Monitor size={14} className="mr-2" />
              Screen Sharing
            </div>
          )}

          {/* Controls Overlay */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-6 py-3 flex items-center space-x-4">
              {/* Audio */}
              <button
                onClick={toggleAudio}
                className={`p-3 rounded-full transition-colors ${
                  videoState.localAudio 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localAudio ? <Mic size={20} /> : <MicOff size={20} />}
              </button>

              {/* Video */}
              <button
                onClick={toggleVideo}
                className={`p-3 rounded-full transition-colors ${
                  videoState.localVideo 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localVideo ? <Video size={20} /> : <VideoOff size={20} />}
              </button>

              {/* Screen Share */}
              <button
                onClick={toggleScreenShare}
                className={`p-3 rounded-full transition-colors ${
                  videoState.screenShare 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                {videoState.screenShare ? <MonitorOff size={20} /> : <Monitor size={20} />}
              </button>

              {/* Recording */}
              <button
                onClick={handleRecordingToggle}
                className={`p-3 rounded-full transition-colors ${
                  videoState.recording 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title={videoState.recording ? 'Stop Recording' : 'Start Recording'}
              >
                {videoState.recording ? <Square size={20} /> : <Circle size={20} />}
              </button>

              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-3 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={20} />
              </button>

              {/* End Call */}
              <button
                onClick={handleEndConsultation}
                className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <Phone size={20} />
              </button>
            </div>
          </div>

          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-lg"
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>

        {/* Side Panel */}
        <div className={`bg-white flex flex-col transition-all duration-300 ${
          showNotes || showChat || showParticipants ? 'w-96' : 'w-0 overflow-hidden'
        }`}>
          {/* Panel Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowParticipants(!showParticipants)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    showParticipants 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Users size={14} className="inline mr-1" />
                  Participants
                </button>
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    showNotes 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FileText size={14} className="inline mr-1" />
                  Notes
                </button>
                <button
                  onClick={() => setShowChat(!showChat)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    showChat 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <MessageSquare size={14} className="inline mr-1" />
                  Chat
                </button>
              </div>
              <button
                onClick={() => {
                  setShowParticipants(false);
                  setShowNotes(false);
                  setShowChat(false);
                }}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Participants Panel */}
          {showParticipants && (
            <div className="p-4 border-b border-gray-200">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Participants ({participants.length})</h3>
                
                {/* Invite Client Button */}
                <button
                  onClick={copyInvitationLink}
                  className={`w-full mb-3 py-2 px-3 rounded-lg text-sm transition-colors flex items-center justify-center ${
                    copiedLink 
                      ? 'bg-green-600 text-white' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copiedLink ? (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Link Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-2" />
                      Copy Client Invitation Link
                    </>
                  )}
                </button>

                <div className="space-y-2">
                  {participants.map(p => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          p.status === 'connected' ? 'bg-green-500' :
                          p.status === 'waiting' ? 'bg-yellow-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500 capitalize">{p.role}</p>
                        </div>
                      </div>
                      {p.role !== 'attorney' && (
                        <div className="flex space-x-1">
                          {p.status === 'waiting' ? (
                            <button className="p-1 text-green-600 hover:bg-green-50 rounded">
                              <UserPlus size={16} />
                            </button>
                          ) : (
                            <button className="p-1 text-red-600 hover:bg-red-50 rounded">
                              <UserX size={16} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Case Notes Panel */}
          {showNotes && (
            <div className="flex-1 flex flex-col p-4">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Case Notes</h3>
                <div className="text-sm text-gray-600 mb-3">
                  <p>Room: {roomId}</p>
                  <p>Date: {new Date().toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex-1">
                <textarea
                  value={caseNotes}
                  onChange={(e) => setCaseNotes(e.target.value)}
                  className="w-full h-full resize-none border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Document case details, client statements, legal issues, next steps..."
                />
              </div>

              <div className="mt-4">
                <button className="w-full bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center">
                  <Save size={14} className="mr-1" />
                  Save Notes
                </button>
              </div>
            </div>
          )}

          {/* Chat Panel */}
          {showChat && (
            <div className="flex-1 flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${
                      msg.sender === 'system' 
                        ? 'text-center' 
                        : msg.sender === 'attorney' 
                          ? 'ml-auto' 
                          : 'mr-auto'
                    }`}
                  >
                    {msg.sender === 'system' ? (
                      <div className="bg-yellow-50 text-yellow-800 text-xs px-3 py-2 rounded-lg border border-yellow-200">
                        {msg.message}
                      </div>
                    ) : (
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                          msg.sender === 'attorney'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-900'
                        }`}
                      >
                        <p>{msg.message}</p>
                        <p className="text-xs opacity-75 mt-1">
                          {msg.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              <div className="border-t border-gray-200 p-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type a message..."
                  />
                  <button
                    onClick={sendMessage}
                    className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};